import React, { useEffect, useMemo } from "react";
import { Form } from "antd";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getGskCopayFormInitialValues } from "./form/initialValues";
import { SaveButton } from "../../header";
import useBaseEEConfiguration from "../../hooks/useBaseEEConfiguration";
import { applyTrimFields } from "../../utils/updateFormData";
import renderReactFromJson from "../../utils/renderReactFromJson";
import { processTemplateVariables } from "../../utils/processTemplateVariables";
import formJson from "./form/GskCopayFormJson.json";
import { getGskIndicationOptions } from "./form/indicationOptions";
import { CompleteFormButton } from "../../CompleteFormButton";
import { HEADER_SUBMIT_BUTTON_TYPES } from "../../constants";
import { SignerTypes } from "../../../../../constant/ESign";
import { DATE_FORMAT, dateFormatter } from "../../../../../utils/date";
import { selectCurrentApplication, selectSelectedAssistanceProgramById } from "../../../../../store/selector";
import { buildTermsAndConditionsLink } from "../../utils/termsAndConditionsLink";

// Several fields in this form register with the form AFTER the initial mount validation, so they
// carry no validation error until something re-validates — which makes their section read as
// "complete" and leaves required fields un-flagged. Three cases, all GSK-specific:
//   • the Caregiver section mounts only for patients under 18 (patient.dob),
//   • the insurance fields mount per Insurance Type (insurance.type) and the SMS-consent checkbox
//     per contact preference (patient.methodOfContact),
//   • the Consent Terms checkbox is a lazy-loaded custom field, so it registers after mount.
// Watch the values that gate these fields and re-validate when they change, keeping required state
// and sidebar completion accurate. Scoped to this form (same Form.useWatch + validateFields pattern
// as UnifiedCaregiverSection) rather than a global change to the shared form/modal.
const GskCopayFormFields = ({ form, formJson }) => {
  const patientDob = Form.useWatch(["patient", "dob"], form);
  const insuranceType = Form.useWatch(["insurance", "type"], form);
  const methodOfContact = Form.useWatch(["patient", "methodOfContact"], form);
  const termsApproval = Form.useWatch(["signature", "copayTermsApproval"], form);

  useEffect(() => {
    form.validateFields().catch(() => {});
  }, [patientDob, insuranceType, methodOfContact, termsApproval, formJson]);

  return renderReactFromJson(form, formJson);
};

const GSK_MIN_AGE_BY_DRUG = Object.freeze({ BENLYSTA: 5, EXDENSUR: 12 });

export const useGskCopayConfiguration = () => {
  const { t } = useTranslation();
  const vendorName = "gsk";
  const eePlanType = "copay";
  const rootPath = "application_form_editor.gsk.copay";

  const currentApplication = useSelector(selectCurrentApplication);
  const selectedAssistanceProgramById = useSelector(selectSelectedAssistanceProgramById);
  const drugDisplayName = currentApplication?.drugDisplayName;
  const drugName = currentApplication?.approvedRxnormDrugs?.[0]?.bnName || drugDisplayName;
  const templateName = currentApplication?.ApplicationFormConfigurationTemplateName;
  const apPid = selectedAssistanceProgramById?.pid;
  const termsAndConditionsLink = buildTermsAndConditionsLink({ templateName, apPid, drugName });

  const processedFormJson = useMemo(() => {
    const withTemplates = processTemplateVariables(formJson, {
      drugName: (drugName || "").toUpperCase(),
      termsAndConditionsLink
    });
    withTemplates.formSections
      .flatMap((s) => s.rows)
      .flatMap((r) => r.fields)
      .filter((f) => f.name === "indication")
      .forEach((f) => {
        f.fieldProps.options = getGskIndicationOptions(drugDisplayName).map((option) => ({
          ...option,
          label: t(option.label)
        }));
      });
    withTemplates.formSections
      .flatMap((s) => s.rows)
      .flatMap((r) => r.fields)
      .filter((f) => (f.rules || []).includes("noFutureDateRule"))
      .forEach((f) => {
        f.rulesParams = {
          ...(f.rulesParams || {}),
          noFutureDateRule: { message: t("application_form_editor.gsk.copay.validation.date_cannot_be_in_the_future") }
        };
      });
    const upperName = (drugName || drugDisplayName || "").toUpperCase();
    const matchedDrug = Object.keys(GSK_MIN_AGE_BY_DRUG).find((key) => upperName.includes(key));
    const minAge = matchedDrug ? GSK_MIN_AGE_BY_DRUG[matchedDrug] : undefined;
    if (minAge !== undefined) {
      withTemplates.formSections
        .filter((s) => s.sectionName === "patient")
        .flatMap((s) => s.rows)
        .flatMap((r) => r.fields)
        .filter((f) => f.name === "dob")
        .forEach((f) => {
          f.rules = [...(f.rules || []), "patientMinAgeRule"];
          f.rulesParams = {
            ...(f.rulesParams || {}),
            patientMinAgeRule: {
              minAge,
              message: t("application_form_editor.gsk.copay.validation.patient_min_age", { minAge })
            }
          };
          // Float this field's error so patientMinAgeRule's message doesn't shift adjacent rows.
          f.dedicatedFieldProps = { ...(f.dedicatedFieldProps || {}), floatError: true };
        });
    }
    return withTemplates;
  }, [drugName, termsAndConditionsLink, drugDisplayName, t]);

  const baseConfig = useBaseEEConfiguration({ vendorName, eePlanType, rootPath, formJson: processedFormJson });

  const gskSpecificConfig = {
    activeFormSection: "",
    getFormInitialValues: ({ applicationForm }) =>
      getGskCopayFormInitialValues({ applicationForm, formJson: processedFormJson }),
    getFormFromJson: (form) => <GskCopayFormFields form={form} formJson={processedFormJson} />,
    saveButton: (props) => (
      <SaveButton onSave={(form) => applyTrimFields(form)} id={"EE_gsk_copay_save_form"} {...props} />
    ),
    afterSubmissionPayload: () => ({}),
    applicationFormHeaderSubmission: (props) => <CompleteFormButton id={"EE_complete_button"} {...props} />,
    headerSubmitButtonType: HEADER_SUBMIT_BUTTON_TYPES.COMPLETE_FORM,

    getSignatureWidgetSettingsBySignerType: (signerType) => {
      const signatureSettings = {
        [SignerTypes.PATIENT]: {
          editable: true,
          titleTranslationKey: "patientApplications.enroll.electronic_signatures.signature_step.signers.patient",
          shouldShowCaregiverIndication: true,
          shouldUseRemoteRequestCheckboxes: false
        },
        [SignerTypes.CAREGIVER]: {
          editable: true,
          titleTranslationKey: "patientApplications.enroll.electronic_signatures.signature_step.signers.caregiver",
          shouldShowCaregiverIndication: true,
          shouldUseRemoteRequestCheckboxes: false
        }
      };
      return signatureSettings[signerType];
    },

    getEnrollResultData: ({ application, t }) => ({
      bin: application.bin,
      pcn: application.pcn,
      group: application.group,
      member_id: application.memberId,
      expiration_date: application.effectiveIndefinitely
        ? t("patientApplications.valid_indefinitely")
        : dateFormatter(application.validThrough) || "--"
    }),

    getAdditionalResultCardInformation: false,
    getApprovedResultDescription: () => <></>,

    successMessage: (t, submittedDate) =>
      t("patientApplications.enroll.application_form_submission.submit_completed", {
        submitDate: moment(submittedDate).format(DATE_FORMAT)
      }),

    shouldShowPdfInSecondStep: true
  };

  return {
    ...baseConfig,
    ...gskSpecificConfig
  };
};

export default useGskCopayConfiguration;
